import React from 'react';
import classNames from 'classnames';

import { getTestId } from 'helpers/test-helpers';

export type BadgeVariant =
  | 'primary'
  | 'secondary'
  | 'neutral'
  | 'critical'
  | 'reserve-outlined'
  | 'highlight-outlined'
  | 'neutral-outlined'
  | 'success-filled'
  | 'critical-filled'
  | 'warning-filled'
  | 'neutral-filled';

interface VariantStyle {
  bgColor: string;
  textColor: string;
  borderClasses?: string;
  dotColor?: string;
}

const VARIANTS: Record<BadgeVariant, VariantStyle> = {
  primary: {
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
  },
  secondary: {
    bgColor: 'bg-success-100',
    textColor: 'text-success-600',
  },
  neutral: {
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
  },
  critical: {
    bgColor: 'bg-critical-600',
    textColor: 'text-white',
  },
  'reserve-outlined': {
    bgColor: 'bg-white',
    textColor: 'text-navy-600',
    borderClasses: 'border border-navy-200',
  },
  'highlight-outlined': {
    bgColor: 'bg-white',
    textColor: 'text-blue-700',
    borderClasses: 'border border-blue-300',
  },
  'neutral-outlined': {
    bgColor: 'bg-white',
    textColor: 'text-gray-600',
    borderClasses: 'border border-gray-300',
    dotColor: 'bg-gray-400',
  },
  'success-filled': {
    bgColor: 'bg-success-100',
    textColor: 'text-success-800',
    borderClasses: 'border border-success-300',
    dotColor: 'bg-success-500',
  },
  'critical-filled': {
    bgColor: 'bg-critical-50',
    textColor: 'text-critical-700',
    borderClasses: 'border border-critical-200',
  },
  'warning-filled': {
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-800',
    borderClasses: 'border border-amber-300',
  },
  'neutral-filled': {
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    borderClasses: 'border border-gray-300',
  },
};

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  text: string | null | undefined;
  icon?: React.ReactNode;
  variant?: BadgeVariant;
  showStatusDot?: boolean;
}

const Badge = ({
  text,
  icon,
  className,
  variant = 'primary',
  showStatusDot = false,
  ...rest
}: BadgeProps): React.JSX.Element | null => {
  if (!text) return null;

  const { bgColor, textColor, borderClasses, dotColor } = VARIANTS[variant];
  const dataTestId = getTestId({
    prefix: 'badge',
    label: text,
    suffix: 'label',
  });

  return (
    <div
      className={classNames(
        'flex h-6 items-center justify-center rounded px-2 gap-x-1',
        bgColor,
        textColor,
        borderClasses,
        className,
      )}
      data-testid={dataTestId}
      {...rest}
    >
      {showStatusDot && (
        <span
          aria-hidden="true"
          className={classNames(
            'size-1.5 rounded-full',
            dotColor ?? 'bg-current',
          )}
          data-testid="badge-status-dot"
        />
      )}
      {icon}
      <span className="text-xs font-semibold">{text}</span>
    </div>
  );
};

export default Badge;
